import React from 'react';
import PropTypes from 'prop-types';
import { Badge } from 'reactstrap';
import { NavLink } from 'react-router-dom';
import { withStyles } from '@material-ui/styles';

import {
  primary1000,
  primary200,
  secondary300,
  disabledText, accentThree200, accentThree1000,
} from 'themes/colors';
import classNames from 'classnames';

const styles = theme => ({
  'sidebar__link-active': {
    borderLeft: `6px solid ${primary1000}`,
    backgroundColor: theme.palette.secondary.light,
    '& $sidebar__link': {
      '& $sidebar__link-icon': {
        color: primary1000,
      },
      '& $sidebar__link-title': {
        color: `${primary1000} !important`,
      },
      '&:before': {
        opacity: '1',
      },
      '&:hover': {
        textDecoration: 'none',
        backgroundColor: theme.palette.secondary.light,
        '&:before': {
          opacity: '1',
        },
      },
    },
  },
  sidebar__link: {
    height: '3.5rem',
    width: '15rem',
    transition: 'all 0.3s',
    position: 'relative',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    padding: '0.688rem 1.25rem',
    overflow: 'hidden',
    background: 'transparent',
    border: 'none',
    '&:before': {
      content: '',
      position: 'absolute',
      left: '0',
      top: '0',
      height: '100%',
      width: '0.25rem',
      background: '$success-color',
      opacity: '0',
      transition: 'all 0.3s',
    },
    '& p': {
      position: 'absolute',
      left: '2.688rem',
      width: '10rem',
      transition: 'left 0.3s',
      top: '50%',
      transform: 'translateY(-50%)',
    },
    '&:hover': {
      textDecoration: 'none',
      backgroundColor: theme.palette.secondary.light,
      '&:before': {
        opacity: '0',
      },
      '& $sidebar__link-icon': {
        color: primary1000,
      },
      '& $sidebar__link-title': {
        color: `${primary1000} !important`,
      },
    },
    '&.collapsed': {
      overflow: 'hidden',
      width: '4rem',
      backgroundColor: 'transparent',
      '& p': {
        position: 'absolute',
        left: '4.375rem',
        width: '10rem',
      },
      '&:hover': {
        backgroundColor: theme.palette.secondary.light,
      },
      '& $sidebar__link-number': {
        opacity: 0,
      },
    },
    '@media screen and (max-width: 576px) ': {
      width: '100%',
      paddingLeft: '0.938rem',
      '& p': {
        top: '50%',
      },
    },
  },
  'sidebar__link-icon': {
    fontSize: '1.25rem',
    lineHeight: '2rem !important',
    color: secondary300,
  },
  'sidebar__link-title': {
    margin: '0',
    marginLeft: '0.938rem',
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    color: `${disabledText} !important`,
    position: 'relative',
    letterSpacing: '-0.013rem',
    display: 'flex',
  },
  'sidebar__link-badge': {
    width: '1.625rem',
    height: '0.875rem',
    backgroundColor: theme.palette.secondary['0'],
    fontSize: '0.5ren',
    fontWeight: '400',
    padding: '0.125ren',
    marginLeft: '0.313rem',
    lineHeight: '0.563rem',
    position: 'relative',
    textTransform: 'uppercase',
    borderRadius: '0.438rem',
    '& span': {
      position: 'absolute',
      left: '0',
      top: '0.188rem',
      width: '1.625rem',
      textAlign: 'center',
    },
  },
  'sidebar__link-number': {
    backgroundColor: primary200,
    color: primary1000,
    borderRadius: '0.375rem',
    padding: '0 0.5rem',
    lineHeight: '1.5rem',
    fontSize: '0.75rem',
    fontWeight: '600',
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    transition: 'all 0.3s',
  },
  'sidebar__link-number-error': {
    backgroundColor: accentThree200,
    color: accentThree1000,
    borderRadius: '0.375rem',
    padding: '0 0.5rem',
    lineHeight: '1.5rem',
    fontSize: '0.75rem',
    fontWeight: '600',
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    transition: 'all 0.3s',
  },
});

const SidebarLink = ({
  title, number, icon, newLink, route, onClick,
  collapsed, classes, isActive, dataIntercomTarget, numberError,
}) => {
  const Icon = icon;
  return (
    <NavLink
      to={route}
      onClick={onClick}
      isActive={isActive}
      activeClassName={classes['sidebar__link-active']}
      data-intercom-target={dataIntercomTarget}
    >
      <li className={classNames(classes.sidebar__link, { collapsed })}>
        {icon && <Icon className={classes['sidebar__link-icon']} />}
        <p className={classes['sidebar__link-title']}>
          {title}
          {newLink ? <Badge className={classes['sidebar__link-badge']}><span>New</span></Badge> : ''}
        </p>
        {number && <div className={classes['sidebar__link-number']}>{number}</div>}
        {numberError && <div className={classes['sidebar__link-number-error']}>{numberError}</div>}
      </li>
    </NavLink>
  );
};

SidebarLink.propTypes = {
  title: PropTypes.string.isRequired,
  number: PropTypes.number,
  numberError: PropTypes.number,
  icon: PropTypes.element,
  newLink: PropTypes.bool,
  route: PropTypes.string,
  onClick: PropTypes.func,
  collapsed: PropTypes.bool.isRequired,
  classes: PropTypes.element.isRequired,
  isActive: PropTypes.func,
  dataIntercomTarget: PropTypes.string,
};

SidebarLink.defaultProps = {
  number: null,
  numberError: null,
  icon: '',
  newLink: false,
  route: '/',
  onClick: () => {},
  isActive: null,
  dataIntercomTarget: null,
};

export default withStyles(styles)(SidebarLink);
